import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Person } from 'types';
import {
  CreateCommentHandler,
  EditCommentHandler,
  ThreadedCalibrationComment,
} from '../types';
import CalibrationCommentsPopoverContent, {
  ToggleVisibilityHandler,
} from './CalibrationCommentsPopoverContent';

interface CommentsModalProps {
  comments: ThreadedCalibrationComment[];
  isOpen: boolean;
  toggle: () => void;
  participant: Person;
  surveyResponseId: number;
  showPrivateComments: boolean;
  onToggleShowPrivate: () => void;
  canAddCalibrationComment: boolean;
  onSubmitComment: CreateCommentHandler;
  onToggleVisibility?: ToggleVisibilityHandler;
  onEditComment?: EditCommentHandler;
  currentUserId?: number;
  isViewAdmin?: boolean;
  highlightedCommentId?: number;
}

const CommentsModal: React.FC<CommentsModalProps> = ({
  comments,
  isOpen,
  toggle,
  participant,
  surveyResponseId,
  showPrivateComments,
  onToggleShowPrivate,
  canAddCalibrationComment,
  onSubmitComment,
  onToggleVisibility,
  onEditComment,
  currentUserId,
  isViewAdmin = false,
  highlightedCommentId,
}) => {
  const { formatMessage } = useIntl();
  const [hasUnsavedEdit, setHasUnsavedEdit] = useState(false);
  const [hasPendingDraftSave, setHasPendingDraftSave] = useState(false);

  const handleToggle = useCallback(() => {
    // New comments and replies are kept as drafts, so only an unsaved edit or
    // a draft that is still being saved would be lost on close
    const warning = hasUnsavedEdit
      ? formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments_modal.unsaved_changes_warning',
          defaultMessage:
            'You have an unsaved comment. Are you sure you want to close?',
        })
      : hasPendingDraftSave
        ? formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments_modal.draft_saving_warning',
            defaultMessage:
              'Your draft is still being saved. Are you sure you want to close?',
          })
        : null;
    if (warning && !window.confirm(warning)) {
      return;
    }
    toggle();
  }, [toggle, formatMessage, hasUnsavedEdit, hasPendingDraftSave]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      size="lg"
      contentClassName="h-100"
    >
      <ModalHeader toggle={handleToggle}>
        <FormattedMessage
          id="app.views.widgets.dashboards.calibration_dashboard.comments_modal.detail_modal.title"
          defaultMessage="Calibration comments for {person}"
          values={{
            person:
              participant?.full_name ??
              formatMessage({
                id: 'app.views.widgets.dashboards.calibration_dashboard.comments.person_placeholder',
                defaultMessage: 'this person',
              }),
          }}
        />
      </ModalHeader>
      <ModalBody
        className="d-flex flex-column"
        style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto' }}
      >
        <CalibrationCommentsPopoverContent
          participant={participant}
          recommendation={'all'}
          comments={comments}
          onToggleShowPrivate={onToggleShowPrivate}
          canAddComment={canAddCalibrationComment}
          onSubmitComment={onSubmitComment}
          onToggleVisibility={onToggleVisibility}
          onEditComment={onEditComment}
          currentUserId={currentUserId}
          isViewAdmin={isViewAdmin}
          variant="modal"
          showPrivateComments={showPrivateComments}
          surveyResponseId={surveyResponseId}
          onUnsavedEditChange={setHasUnsavedEdit}
          onPendingDraftSaveChange={setHasPendingDraftSave}
          highlightedCommentId={highlightedCommentId}
        />
      </ModalBody>
    </Modal>
  );
};

export default CommentsModal;
